import {
  flexRender,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";

import { T } from "@repo/transifex";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "~/components/ui/table";

/** Use this type due to type checking issues. any value it's not permitted in this definition ColumnDef<T, any>[] */
type ColumnDef<T> = Parameters<typeof useReactTable<T>>[0]["columns"];

type DataTableProps<T> = {
  columns: ColumnDef<T>;
  data: T[];
};

export const TransactionsDataTable = <T extends object>({
  columns,
  data,
  isLoading
}: DataTableProps<T> & { isLoading?: boolean }) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <Table>
      <TableHeader className="bg-[#F4F6F4]">
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TransactionsDataTableSkeleton
            numberOfColumns={columns.length}
            numberOfRows={7}
          />
        ) : (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}

        {!isLoading && table.getRowModel().rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={columns.length} className="h-24 text-center">
              <T _str="No results" />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

const TransactionsDataTableSkeleton = ({
  numberOfColumns,
  numberOfRows
}: {
  numberOfColumns: number;
  numberOfRows: number;
}) => {
  return Array.from({ length: numberOfRows }).map((_, index) => (
    // eslint-disable-next-line react/no-array-index-key -- There is no concern about performance for loading rows
    <TableRow key={`transaction-loading-${index}`}>
      <TableCell colSpan={numberOfColumns}>
        <div className="h-5 w-full animate-pulse rounded-md bg-gray-400" />
      </TableCell>
    </TableRow>
  ));
};
